import { PCGLQueryKeys } from '../shared/constants';

export * from './data-plane-merchant-schema';
export enum EndType {
	Never = 'Never',
	Date = 'Date',
	Occurrences = 'Occurrences',
}

export type WidgetInputs = Partial<Record<keyof typeof PCGLQueryKeys, string>> & {
	GiftType: string;
};

export type WidgetInputsWithCustomFields = WidgetInputs & { [key: string]: string };

export enum StyleState {
	idle = 'idle',
	loaded = 'loaded',
	failed = 'failed',
}

export enum Currency {
	NZD = 0,
	AUD = 1,
	USD = 2,
	CAD = 3,
	Unspecified = -1,
}

export enum TextFieldValidation {
	NoValidation = 0,
	LettersOnly = 1,
}

export enum FrequencyCode {
	Other = 0,
	Weekly = 1,
	Fortnightly = 2,
	Monthly = 3,
	FirstAndFifteenth = 4,
	OneMinute = 5,
	FiveMinutes = 6,
	FourHours = 7,
	Yearly = 8,
	SemiYearly = 9,
	Quarterly = 10,
}

export interface PaymentLabel {
	NounSentenceCase: string;
	NounPluralSentenceCase: string;
	VerbSentenceCase: string;
	VerbAppliedSentenceCase: string;
	NounLowerCase: string;
	NounPluralLowerCase: string;
	VerbLowerCase: string;
	VerbAppliedLowerCase: string;
	VerbPresentTenseSentenceCase: string;
	VerbPresentTenseLowerCase: string;
	VerbPastTenseSentenceCase: string;
	VerbPastTenseLowerCase: string;
	PayerSentenceCase: string;
	PayerLowerCase: string;
	PayerPluralSentenceCase: string;
	PayerPluralLowerCase: string;
	ActionSentenceCase: string;
	ActionLowerCase: string;
	ActionPluralSentenceCase: string;
	ActionPluralLowerCase: string;
	NonCashNounSentenceCase: string;
	NonCashNounLowerCase: string;
	NonCashPluralSentenceCase: string;
	NonCashPluralLowerCase: string;
	TextGivingFeatureName: string;
	GiftEntryFeatureName: string;
}

export type StartApplePaySessionRequest = {
	organizationKey: string;
	validationUri: string;
	name: string;
	domain: string;
};

export type APSessionResponseSuccess = { success: boolean; blob: string };
export type APSessionResponseFailure = { message: string };
export type ApplePaySessionResponse = APSessionResponseSuccess | APSessionResponseFailure;

export type MakeApplePayPaymentRequest = {
	applePayInfo: {
		applePayPaymentToken: {
			paymentData: string;
			paymentMethod: {
				displayName: string;
				network: string;
				type: number;
				paymentPass: {
					primaryAccountIdentifier: string;
					primaryAccountNumberSuffix: string;
					activationState: number;
				};
			};
			transactionIdentifier: string;
		};
		applePayBillingContact: {
			addressLine1: string;
			addressLine2: string;
			city: string;
			state: string;
			zip: string;
			country: string;
		};
	};
	customFields: { key: string; value: string }[];
	emailAddress: string;
	firstName: string;
	lastName: string;
	merchantKey: string;
	lineItems: { fundKey: string; amount: number; amountWithFees: number }[];
	mobileNumber: string;
};

// Sourced from the QuickpayRelayService
// https://github.com/pushpay/platform-services/tree/master/services/QuickpayRelayService
// ApplePayPaymentCompleteResponseModel.cs

export enum QuickPayRelayResultStatus {
	Success = 0,
	Fail = 1,
	RequiresConfirmation = 2,
	Poll = 3,
	Processing = 4,
	TimedOut = 5,
	CardFail = 6,
	SuspectedAccidentalDuplicate = 7,
	InvalidAmount = 8,
	MaximumYearExceeded = 9,
	InvalidReferences = 10,
	PaymentMethodNotSupported = 11,
	Delayed = 12,
	InvalidSignedConfigWithRedirectUrl = 13,
	InvalidInputFields = 100,
}

export enum QuickPayRelayUserInvalidFields {
	EmailAddress = 'EmailAddress',
	FirstName = 'FirstName',
	LastName = 'LastName',
	MobileNumber = 'MobileNumber',
	Amount = 'Amount',
}

export enum QuickPayRelayInternalInvalidFields {
	AmountWithFees = 'AmountWithFees',
	FundKey = 'FundKey',
	LineItems = 'LineItems',
	ApplePayInfo = 'ApplePayInfo',
	MerchantKey = 'MerchantKey',
	UserAgent = 'UserAgent',
	ApplePayPaymentToken = 'ApplePayPaymentToken',
	PaymentData = 'PaymentData',
	Signature = 'Signature',
	TransactionIdentifier = 'TransactionIdentifier',
}

export type MakeApplePayPaymentResponse = {
	success: boolean;
	errorMessages?: string[] | null;
	status: QuickPayRelayResultStatus;
	statusDescription: string;
	invalidFields: string[];
};

export type CustomFieldKeys = Record<string, string>;
